import { IPriceLineItem, TransactionPricingResult } from 'Services/Api/_HumanWritten/PricingService/PricingResult';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';

/**
 * The figures the summary total card prints, worked out from the transaction's pricing. Shared by
 * `TripBookingSummaryTotal` and `ClassBookingSummaryTotal` so the two cards cannot disagree about a fee:
 * they differ only in layout.
 */
export interface SummaryTotalFigures {
	totalAlterationFee: number;
	totalTrailerFee: number;
	totalCheckInForfeitFee: number;
	totalRemovalFee: number;
	/** Read off the server's line: the wording is tenant-configurable (`check-in-forfeit-fee__label`). */
	checkInForfeitFeeLabel: string;
	/** Read off the server's line: configurable through `removal-fee__label`. */
	removalFeeLabel: string;
	departurePriceIncrease: number;
	returnPriceIncrease: number;
	basePriceIncrease: number;
	gst: number;
	/** Server parking, surfaced in the subtotal. Already inside the server's total. */
	parkingPriceForSubtotal: number;
	/** See `feesListedBelowSubtotal` below. */
	feesListedBelowSubtotal: number;
}

/**
 * The waivable increase for one leg: the cargo / trailer / trip-change rises, less any drop on that
 * leg's passenger line.
 *
 * Only the *drop* is netted off. With `DriverlessVehicle.IncludesOneAdultPrice` on, removing a
 * vehicle's last adult moves the cargo line up by exactly the adult fare and the passenger line down
 * by the same amount — a $0 alteration. Summing the rises alone reported a "Ticket price increase" of
 * a full adult fare on that $0 edit, and the manager's remove button discounts by whatever this
 * figure says, so waiving it credited the customer money they were never charged.
 *
 * A passenger line that has gone *up* is not netted in: that is a seat the customer chose to add,
 * not a price rise for a manager to waive.
 */
const legPriceIncrease = (lineItems: IPriceLineItem[] | undefined) => {
	const priceFor = (key: string) => lineItems?.find(y => y.key === key)?.price ?? 0;

	const rises = priceFor('BaseCargo') + priceFor('BaseTowOn') + priceFor('TicketPriceIncrease');
	const passengerDrop = Math.min(priceFor('Passenger'), 0);

	return Math.max(0, rises + passengerDrop);
};

export function summaryTotalFigures(
	transactionPricingResults: TransactionPricingResult | undefined,
	wizardData: BookingWizardData | undefined,
	isStaff: boolean,
): SummaryTotalFigures {
	let departurePriceIncrease = 0;
	let returnPriceIncrease = 0;
	let totalAlterationFee = 0;
	let basePriceIncrease = 0;
	let totalTrailerFee = 0;
	let totalCheckInForfeitFee = 0;
	let totalRemovalFee = 0;
	let checkInForfeitFeeLabel = '';
	let removalFeeLabel = '';

	// The booking-success page renders the card with no wizardData at all (the wizard is finished), so
	// gating on wizardMode alone left totalAlterationFee and totalTrailerFee at zero there and neither fee
	// row ever rendered. The server already says whether the transaction is an alteration; fall back to
	// it, the same way TripBookingSummaryInner does.
	const isAlterationTransaction =
		wizardData?.wizardMode === 'ALTERATION' || (transactionPricingResults?.isAlteration ?? false);

	if (transactionPricingResults?.linkedBookings !== null && isAlterationTransaction) {
		transactionPricingResults?.linkedBookings?.forEach(x => {
			totalAlterationFee += x.departureBooking.priceLineItems.find(y => y.key === 'AlterationFee')?.price ?? 0;
			departurePriceIncrease += legPriceIncrease(x.departureBooking.priceLineItems);
			totalTrailerFee +=
				x.departureBooking.priceLineItems.find(y => y.key === 'TowOnCancellationFee')?.price ?? 0;

			totalAlterationFee += x.returnBooking?.priceLineItems?.find(y => y.key === 'AlterationFee')?.price ?? 0;
			returnPriceIncrease += legPriceIncrease(x.returnBooking?.priceLineItems);
			totalTrailerFee += x.returnBooking?.priceLineItems.find(y => y.key === 'TowOnCancellationFee')?.price ?? 0;

			const departureForfeit = x.departureBooking.priceLineItems.find(y => y.key === 'CheckInForfeitFee');
			const returnForfeit = x.returnBooking?.priceLineItems.find(y => y.key === 'CheckInForfeitFee');

			totalCheckInForfeitFee += departureForfeit?.price ?? 0;
			totalCheckInForfeitFee += returnForfeit?.price ?? 0;

			checkInForfeitFeeLabel =
				departureForfeit?.description || returnForfeit?.description || checkInForfeitFeeLabel;

			const departureRemoval = x.departureBooking.priceLineItems.find(y => y.key === 'RemovalFee');
			const returnRemoval = x.returnBooking?.priceLineItems.find(y => y.key === 'RemovalFee');

			totalRemovalFee += departureRemoval?.price ?? 0;
			totalRemovalFee += returnRemoval?.price ?? 0;

			removalFeeLabel = departureRemoval?.description || returnRemoval?.description || removalFeeLabel;

			// It is the return booking for a two-way booking. Optional now that this block also runs with
			// no wizardData (booking-success): with none, the legs are not swapped, which only affects the
			// manager ticket-price-increase split — an affordance booking-success does not render anyway.
			if (wizardData?.departureTrip === false) {
				returnPriceIncrease = departurePriceIncrease;
				departurePriceIncrease = 0;
			}
			basePriceIncrease = departurePriceIncrease + returnPriceIncrease;
		});
	}

	const gst = transactionPricingResults?.lines?.find(x => x.key === 'Gst')?.price ?? 0;

	// Parking price is taken solely from the server line items on this transaction.
	//
	// We deliberately do NOT fall back to the standalone parking wizard's Zustand store here. That
	// store is shared with the separate Purchase-Parking flow and is only reset on modal open / timer
	// expiry (not on a plain close), and never reloaded (it's in-memory). So an abandoned parking
	// quote leaves a stale totalPrice behind, which used to be added onto this ferry cart's total and
	// subtotal — inflating the cart total above the summed line items while the payment tab (which
	// reads the authoritative backend total) stayed correct. Parking is no longer part of the booking
	// wizard (the in-wizard add-on section is disabled), so the only legitimate parking figure is a
	// persisted server 'Parking' line item, already reflected in totalPrice.
	const parkingPriceForSubtotal =
		transactionPricingResults?.lines?.filter(x => x.key === 'Parking').reduce((sum, item) => sum + item.price, 0) ??
		0;

	/**
	 * The fee rows the card prints BENEATH the subtotal, which the card's layout reads as additive:
	 * Subtotal, then each fee, then TOTAL.
	 *
	 * `ServiceFee` really is additive — the server marks it `includeInSubtotalPrice: false`. The
	 * alteration fee and the trailer removal fee are not: both are inside the server's subtotal already,
	 * because the tax invoice prints them as rows above its own Subtotal and needs them counted there.
	 * So the same number arrived twice on this card and the column stopped adding up — a $20 trailer
	 * removed with a $20 fee showed Subtotal $0.00, "Trailer removal fee $20.00", TOTAL $0.00.
	 *
	 * Take them back out of the displayed subtotal rather than off the total: the total is what is
	 * charged and must not move. Only what is actually rendered is subtracted, so a customer — who sees
	 * no fee rows — keeps a subtotal that still reconciles to their total.
	 */
	const feesListedBelowSubtotal =
		(isStaff ? Math.max(totalAlterationFee, 0) : 0) +
		Math.max(totalTrailerFee, 0) +
		Math.max(totalCheckInForfeitFee, 0) +
		Math.max(totalRemovalFee, 0);

	return {
		totalAlterationFee,
		totalTrailerFee,
		totalCheckInForfeitFee,
		totalRemovalFee,
		checkInForfeitFeeLabel,
		removalFeeLabel,
		departurePriceIncrease,
		returnPriceIncrease,
		basePriceIncrease,
		gst,
		parkingPriceForSubtotal,
		feesListedBelowSubtotal,
	};
}
